import * as React from 'react';
import { useLoadNativeAd } from '../hooks/native';
import {
    NativeAdObj,
    Status,
    DeviceType,
    Vertical,
    Subvertical,
    PageType,
    Features,
} from '../../shared/types/ast';
import { Content, NativeVideo, Netboard } from '.';
import { getDeviceType } from '../utils/device';
import { getConfig } from '../config';
import Comment from './sub/comment';
import MainMedia from './sub/main-media';
import Link from './sub/link';
import Title from './sub/title';
import Description, { getDescription } from './sub/description';
import Logo from './sub/logo';
import Badge from './sub/badge';
import TransparencyButton from './sub/transparencyButton';
import { ListLogoSection, MobileListLogoSection } from './netboard';

export type Props = {
    adObj: NativeAdObj;
    status: Status;
    id: string;
    className?: string;
    grid: boolean;
    tempRecircHack?: boolean;
    vertical?: Vertical;
    subvertical?: Subvertical;
    pageType?: PageType;
    deviceType: DeviceType;
    features?: Features;
    isRecirc?: boolean;
};

export function getNativeType(adObj: NativeAdObj) {
    if (adObj.native) {
        if (adObj.native.video && adObj.native.video.content) {
            return 'video';
        }
        if (adObj.native.desc2) {
            return adObj.native.desc2;
        }
        if (adObj.native.custom) {
            return adObj.native.custom.type;
        }
    }
    return '';
}

function FabricNative(props: Props) {
    const { adObj, deviceType, grid } = props;
    const { native } = adObj;
    const isMobileList = deviceType === 'mobile' && !grid;

    const isDescriptionEmpty = !(getDescription(native).length > 0);

    const determineFlexDirection = () => {
        if (grid && isDescriptionEmpty) {
            return 'row';
        }
        if (isMobileList && isDescriptionEmpty) {
            return 'row';
        } else return 'column';
    };

    const renderListLogoComponent = () =>
        isMobileList ? (
            <MobileListLogoSection
                native={native}
                isDescriptionEmpty={isDescriptionEmpty}
                className={'text-12'}
            />
        ) : (
            <ListLogoSection
                native={native}
                isDescriptionEmpty={isDescriptionEmpty}
                className={'text-12 pr-8'}
            />
        );
    return (
        <div>
            <Comment placement="NewNativeNetboard" adObj={adObj} />
            <div aria-owns={`result-item-heading-${adObj.targetId}`} />
            <div
                className={`relative rounded-8 border border-bluegray-200 mb-8`}
            >
                <MainMedia native={native} className="rounded-8" />
            </div>
            <div className="flex flex-col break-words">
                <div
                    style={{
                        display: 'flex',
                        flexDirection: determineFlexDirection(),
                        height: grid ? '80%' : '95%',
                    }}
                >
                    <h2
                        className="text-16"
                        id={`result-item-heading-${adObj.targetId}`}
                    >
                        <Link
                            adObj={adObj}
                            overrideClassName={
                                'text-gray-700 hover:no-underline focus:no-underline focus-ring'
                            }
                        >
                            <span
                                aria-hidden="true"
                                className="absolute inset-0"
                            />
                            <Title native={adObj.native} className="pt-8" />
                        </Link>
                    </h2>
                    {grid ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Description
                                native={native}
                                className="ads__unit__content__list pr-8"
                            />
                            <Logo
                                native={native}
                                style={{
                                    maxHeight: '48px',
                                    maxWidth: '75px',
                                    marginBottom: isDescriptionEmpty
                                        ? '8px'
                                        : undefined,
                                    marginLeft: 'auto',
                                }}
                            />
                        </div>
                    ) : (
                        renderListLogoComponent()
                    )}
                </div>
                <div className="order-first text-12">
                    <Badge
                        native={adObj.native}
                        style={{ color: 'rgb(118, 118, 118)' }}
                    />
                    <div className="absolute right-0">
                        <TransparencyButton
                            adObj={adObj}
                            style={{
                                filter: '',
                                zIndex: 1,
                                opacity: 0.6,
                            }}
                            placement="right"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export function Native(props: Props) {
    const {
        vertical: _vertical,
        subvertical: _subvertical,
        pageType: _pageType,
        deviceType: _deviceType,
    } = getConfig();

    const {
        adObj,
        id,
        className,
        status,
        vertical = _vertical,
        subvertical = _subvertical,
        pageType = _pageType,
        deviceType = _deviceType,
        grid,
    } = props;

    useLoadNativeAd(adObj, status);
    const type = getNativeType(adObj);
    const { layoutHasNoTroika } = getConfig();

    const fabricNativeStyling =
        props.isRecirc || (layoutHasNoTroika && pageType === 'object');

    let defaultClassName = 'ads__unit ads__unit--native';

    if (fabricNativeStyling) {
        return <FabricNative {...props} />;
    }
    switch (type) {
        case 'content':
            const list = !grid && deviceType !== 'mobile';
            defaultClassName =
                'ads__unit ads__unit--native ads__unit--fullwidth';
            if (list) {
                defaultClassName =
                    'ads__unit ads__unit--native ads__unit--fullwidth p-0';
            }
            if (subvertical === 'holiday_rental') {
                defaultClassName =
                    'ads__unit ads__unit--native ads__unit--fullwidth p-0 pb-16';
            }
            return (
                <div
                    id={id}
                    className={className || defaultClassName}
                    style={{ display: 'block' }}
                >
                    <Content adObj={adObj} list={list} />
                </div>
            );
        case 'video':
            return (
                <div
                    id={id}
                    className={
                        className ||
                        'ads__unit ads__unit--native ads__unit--fullwidth'
                    }
                >
                    <NativeVideo
                        adObj={adObj}
                        mobile={getDeviceType() === 'mobile'}
                    />
                </div>
            );

        default:
            if (subvertical === 'holiday_rental') {
                defaultClassName = 'ads__unit ads__unit--native p-0 pb-16';
            }
            if (subvertical === 'flight') {
                defaultClassName = 'ads__unit ads__unit--native py-32';
            }
            if (subvertical === 'prepackage') {
                if (pageType === 'object') {
                    if (deviceType === 'mobile') {
                        defaultClassName = 'mx-0 my-16';
                    } else {
                        defaultClassName = 'ads__unit ads__unit--native p-0';
                    }
                } else {
                    defaultClassName = 'ads__unit ads__unit--native p-2';
                }
            }

            return (
                <div id={`${id}`} className={className || defaultClassName}>
                    <Netboard
                        adObj={adObj}
                        grid={grid}
                        vertical={vertical}
                        subvertical={subvertical}
                        pageType={pageType}
                        deviceType={deviceType}
                    />
                </div>
            );
    }
}
